import React, { useState } from 'react'
import Plot from 'react-plotly.js'

import { makeStyles } from '@material-ui/core/styles'

import {
    IssueAnalyticsData,
    IssueAnalyticsLoadingState,
} from '../../hooks/useIssueAnalytics'
import ChartToggleButton from '../ServiceRequestAnalytics/ChartToggleButton'

const useStyles = makeStyles({
    analyticsDashboard: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px',
        padding: '20px',
        height: 'calc(100vh - 120px)',
        overflow: 'auto',
    },
    chartContainer: {
        flex: '1 1 calc(50% - 24px)',
        minWidth: '400px',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        height: '500px',
        '& h3': {
            marginTop: 0,
            marginBottom: '16px',
        },
        '& .js-plotly-plot': {
            flex: 1,
        },
    },
    chartHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        '& h3': {
            margin: 0,
        },
    },
})

interface IssueChartsProps {
    loadingState: IssueAnalyticsLoadingState
    analytics: IssueAnalyticsData
}

const IssueCharts: React.FC<IssueChartsProps> = ({
    analytics,
    loadingState,
}) => {
    const classes = useStyles()
    const [showTimeGraphs, setShowTimeGraphs] = useState({
        tenants: false,
        locations: false,
        inventories: false,
        services: false,
    })

    // Transform and sort data for tenants bar chart
    const tenantsData = (() => {
        const sortedTenants = analytics.top_tenants
            .sort((a, b) => b.total_count - a.total_count)
            .slice(0, 10) // Show top 10

        return {
            x: sortedTenants.map((item) => item.total_count),
            y: sortedTenants.map((item) => item.name),
            type: 'bar' as const,
            orientation: 'h' as const,
            marker: {
                color: '#1f77b4',
                opacity: 0.8,
            },
            name: 'Tenants',
        }
    })()

    // Transform and sort data for locations pie chart
    const locationsData = (() => {
        const sortedLocations = analytics.top_locations
            .sort((a, b) => b.total_count - a.total_count)
            .slice(0, 10) // Show top 10

        return {
            values: sortedLocations.map((item) => item.total_count),
            labels: sortedLocations.map((item) => item.name),
            type: 'pie' as const,
            hole: 0.4,
            name: 'Locations',
        }
    })()

    // Transform and sort data for inventories bar chart
    const inventoriesData = (() => {
        const sortedInventories = analytics.top_inventories
            .sort((a, b) => b.total_count - a.total_count)
            .slice(0, 10) // Show top 10

        return {
            x: sortedInventories.map((item) => item.total_count),
            y: sortedInventories.map((item) => item.name),
            type: 'bar' as const,
            orientation: 'h' as const,
            marker: {
                color: '#2ecc71',
                opacity: 0.8,
            },
            name: 'Inventories',
        }
    })()

    // Transform and sort data for services bar chart
    const servicesData = (() => {
        const sortedServices = analytics.top_services
            .sort((a, b) => b.total_count - a.total_count)
            .slice(0, 10) // Show top 10

        return {
            x: sortedServices.map((item) => item.total_count),
            y: sortedServices.map((item) => item.name),
            type: 'bar' as const,
            orientation: 'h' as const,
            marker: {
                color: '#ff7f0e',
                opacity: 0.8,
            },
            name: 'Services',
        }
    })()

    // Transform vendor data for comparison chart
    const vendorComparisonData = (() => {
        const sortedVendors = Object.entries(analytics.vendor_data).sort(
            ([, a], [, b]) => b.assigned_count - a.assigned_count,
        )

        return [
            {
                x: sortedVendors.map(([vendor]) => vendor),
                y: sortedVendors.map(([, data]) => data.assigned_count),
                name: 'Assigned',
                type: 'bar' as const,
                marker: { color: '#17a2b8' },
            },
            {
                x: sortedVendors.map(([vendor]) => vendor),
                y: sortedVendors.map(([, data]) => data.completed_count),
                name: 'Completed',
                type: 'bar' as const,
                marker: { color: '#28a745' },
            },
        ]
    })()

    // Transform data for daily trends
    const dailyTrendsData = [
        {
            x: analytics.assigned_daily.map((item) => item.date),
            y: analytics.assigned_daily.map((item) => item.count),
            type: 'scatter' as const,
            mode: 'lines+markers' as const,
            name: 'Assigned',
            line: { color: '#17a2b8' },
            marker: { size: 6 },
        },
        {
            x: analytics.completed_daily.map((item) => item.date),
            y: analytics.completed_daily.map((item) => item.count),
            type: 'scatter' as const,
            mode: 'lines+markers' as const,
            name: 'Completed',
            line: { color: '#28a745' },
            marker: { size: 6 },
        },
    ]

    // Get time series data for a specific category
    const getTimeSeriesData = (items: typeof analytics.top_tenants) => {
        const sortedItems = items
            .sort((a, b) => b.total_count - a.total_count)
            .slice(0, 10) // Show top 10 for time series

        return sortedItems.map((item) => ({
            x: item.date_data.map((point) => point.date),
            y: item.date_data.map((point) => point.count),
            type: 'scatter' as const,
            mode: 'lines+markers' as const,
            name: item.name,
        }))
    }

    const toggleTimeGraph = (
        type: 'tenants' | 'locations' | 'inventories' | 'services',
    ) => {
        setShowTimeGraphs((prev) => ({
            ...prev,
            [type]: !prev[type],
        }))
    }

    return (
        <div className={classes.analyticsDashboard}>
            {/* Top Tenants Section */}
            <div className={classes.chartContainer}>
                <div className={classes.chartHeader}>
                    <h3>Top Tenants by Issues</h3>
                    <ChartToggleButton
                        showTrend={showTimeGraphs.tenants}
                        onChange={() => toggleTimeGraph('tenants')}
                    />
                </div>
                <Plot
                    data={
                        showTimeGraphs.tenants
                            ? getTimeSeriesData(analytics.top_tenants)
                            : [tenantsData]
                    }
                    layout={{
                        height: 400,
                        margin: {
                            l: 50,
                            r: 40,
                            t: 20,
                            b: showTimeGraphs.tenants ? 80 : 40,
                        },
                        yaxis: {
                            title: showTimeGraphs.tenants
                                ? { text: 'Number of Issues' }
                                : { text: 'Tenant' },
                            automargin: true,
                        },
                        xaxis: showTimeGraphs.tenants
                            ? {
                                  title: { text: 'Date', standoff: 30 },
                                  type: 'date',
                                  tickangle: -45,
                                  automargin: true,
                                  tickfont: { size: 10 },
                              }
                            : {
                                  title: { text: 'Number of Issues' },
                              },
                        showlegend: showTimeGraphs.tenants,
                        legend: showTimeGraphs.tenants
                            ? {
                                  orientation: 'h',
                                  y: -0.3,
                              }
                            : undefined,
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Top Locations Section */}
            <div className={classes.chartContainer}>
                <div className={classes.chartHeader}>
                    <h3>Top Issue Locations</h3>
                    <ChartToggleButton
                        showTrend={showTimeGraphs.locations}
                        onChange={() => toggleTimeGraph('locations')}
                    />
                </div>
                <Plot
                    data={
                        showTimeGraphs.locations
                            ? getTimeSeriesData(analytics.top_locations)
                            : [locationsData]
                    }
                    layout={{
                        height: 400,
                        margin: {
                            l: showTimeGraphs.locations ? 50 : 20,
                            r: 20,
                            t: 20,
                            b: showTimeGraphs.locations ? 80 : 20,
                        },
                        showlegend: true,
                        legend: {
                            orientation: 'h',
                            y: showTimeGraphs.locations ? -0.3 : -0.1,
                        },
                        yaxis: showTimeGraphs.locations
                            ? {
                                  title: { text: 'Number of Issues' },
                                  automargin: true,
                              }
                            : {},
                        xaxis: showTimeGraphs.locations
                            ? {
                                  title: { text: 'Date', standoff: 30 },
                                  type: 'date',
                                  tickangle: -45,
                                  automargin: true,
                                  tickfont: { size: 10 },
                              }
                            : {},
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Top Inventories Section */}
            <div className={classes.chartContainer}>
                <div className={classes.chartHeader}>
                    <h3>Top Issue Types (Inventories)</h3>
                    <ChartToggleButton
                        showTrend={showTimeGraphs.inventories}
                        onChange={() => toggleTimeGraph('inventories')}
                    />
                </div>
                <Plot
                    data={
                        showTimeGraphs.inventories
                            ? getTimeSeriesData(analytics.top_inventories)
                            : [inventoriesData]
                    }
                    layout={{
                        height: 400,
                        margin: {
                            l: 50,
                            r: 40,
                            t: 20,
                            b: showTimeGraphs.inventories ? 80 : 40,
                        },
                        yaxis: {
                            title: showTimeGraphs.inventories
                                ? { text: 'Number of Issues' }
                                : { text: 'Issue Type' },
                            automargin: true,
                        },
                        xaxis: showTimeGraphs.inventories
                            ? {
                                  title: { text: 'Date', standoff: 30 },
                                  type: 'date',
                                  tickangle: -45,
                                  automargin: true,
                                  tickfont: { size: 10 },
                              }
                            : {
                                  title: { text: 'Number of Issues' },
                              },
                        showlegend: showTimeGraphs.inventories,
                        legend: showTimeGraphs.inventories
                            ? {
                                  orientation: 'h',
                                  y: -0.3,
                              }
                            : undefined,
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Daily Trends Chart */}
            <div className={classes.chartContainer}>
                <h3>Daily Issue Trends</h3>
                <Plot
                    data={dailyTrendsData}
                    layout={{
                        height: 400,
                        margin: { l: 50, r: 20, t: 20, b: 80 },
                        xaxis: {
                            title: {
                                text: 'Date',
                                standoff: 30,
                            },
                            tickangle: -45,
                            type: 'date',
                            tickformat: '%Y-%m-%d',
                            automargin: true,
                            tickfont: { size: 10 },
                            ticks: '',
                            showticksuffix: 'all',
                            ticksuffix: '  ',
                        },
                        yaxis: {
                            title: {
                                text: 'Number of Issues',
                                standoff: 20,
                            },
                            rangemode: 'tozero',
                        },
                        legend: {
                            orientation: 'h',
                            y: -0.3,
                        },
                        hovermode: 'x unified',
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Top Services Section */}
            <div className={classes.chartContainer}>
                <div className={classes.chartHeader}>
                    <h3>Top Services</h3>
                    <ChartToggleButton
                        showTrend={showTimeGraphs.services}
                        onChange={() => toggleTimeGraph('services')}
                    />
                </div>
                <Plot
                    data={
                        showTimeGraphs.services
                            ? getTimeSeriesData(analytics.top_services)
                            : [servicesData]
                    }
                    layout={{
                        height: 400,
                        margin: {
                            l: 50,
                            r: 40,
                            t: 20,
                            b: showTimeGraphs.services ? 80 : 40,
                        },
                        yaxis: {
                            title: showTimeGraphs.services
                                ? { text: 'Number of Issues' }
                                : { text: 'Service Type' },
                            automargin: true,
                        },
                        xaxis: showTimeGraphs.services
                            ? {
                                  title: { text: 'Date', standoff: 30 },
                                  type: 'date',
                                  tickangle: -45,
                                  automargin: true,
                                  tickfont: { size: 10 },
                              }
                            : {
                                  title: { text: 'Number of Issues' },
                              },
                        showlegend: showTimeGraphs.services,
                        legend: showTimeGraphs.services
                            ? {
                                  orientation: 'h',
                                  y: -0.3,
                              }
                            : undefined,
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Vendor Performance Comparison */}
            <div className={classes.chartContainer}>
                <h3>Vendor Assignments</h3>
                <Plot
                    data={vendorComparisonData}
                    layout={{
                        height: 400,
                        margin: { l: 50, r: 20, t: 20, b: 150 },
                        barmode: 'group',
                        xaxis: {
                            tickangle: -45,
                            title: {
                                text: 'Vendor',
                                standoff: 30,
                            },
                            automargin: true,
                            tickfont: { size: 10 },
                            ticks: '',
                            showticksuffix: 'all',
                            ticksuffix: '  ',
                        },
                        yaxis: {
                            title: {
                                text: 'Number of Issues',
                                standoff: 20,
                            },
                        },
                        legend: {
                            orientation: 'h',
                            y: -0.4,
                        },
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>
        </div>
    )
}

export default IssueCharts
